import type { ReactNode } from 'react'
import { Select } from '@systemeio/components'
import { ChevronDownIcon } from '@systemeio/icons'
import type { TFunction } from 'next-i18next'
import { useTranslation } from 'next-i18next'
import { twMerge } from 'tailwind-merge'
import type { CommentSortEnum } from 'modules/comments/types'
import { CommentSortOptions } from 'modules/comments/types'

interface CommentSortSelectorProps {
  currentSort: CommentSortEnum
  onSortChange: (sortOption: CommentSortEnum) => void
  className?: string
  disabled?: boolean
}

const getSortOptionLabel = (sortOption: CommentSortEnum, t: TFunction): string => {
  switch (sortOption) {
    case CommentSortOptions.NEWEST_FIRST: {
      return t('comments.sort.newest_first')
    }
    case CommentSortOptions.OLDEST_FIRST: {
      return t('comments.sort.oldest_first')
    }
    case CommentSortOptions.POPULAR: {
      return t('comments.sort.popular')
    }
    default: {
      return t('comments.sort.newest_first')
    }
  }
}

export const CommentSortSelector = ({
  currentSort,
  onSortChange,
  className,
  disabled = false,
}: CommentSortSelectorProps): ReactNode => {
  const { t } = useTranslation()
  const sortOptions = [
    CommentSortOptions.NEWEST_FIRST,
    CommentSortOptions.OLDEST_FIRST,
    CommentSortOptions.POPULAR,
  ]

  return (
    <div className={twMerge('flex flex-wrap items-center gap-4', className)}>
      <p className="shrink-0 font-semibold whitespace-nowrap text-text-primary">
        {t('comments.sort.label')}
      </p>
      <div className="w-fit max-w-full min-w-0">
        <Select.Root
          items={sortOptions.map(sortOption => ({
            value: sortOption,
            label: getSortOptionLabel(sortOption, t),
          }))}
          value={currentSort}
          disabled={disabled}
          onValueChange={value => {
            if (value != null) {
              onSortChange(value as CommentSortEnum)
            }
          }}
        >
          <Select.Trigger size="sm" className="w-fit max-w-full">
            <Select.Value />
            <Select.Icon>
              <ChevronDownIcon className="size-4" />
            </Select.Icon>
          </Select.Trigger>
          <Select.Content>
            <Select.List>
              {sortOptions.map(sortOption => (
                <Select.Item key={sortOption} value={sortOption}>
                  <Select.ItemText>{getSortOptionLabel(sortOption, t)}</Select.ItemText>
                  <Select.ItemIndicator />
                </Select.Item>
              ))}
            </Select.List>
          </Select.Content>
        </Select.Root>
      </div>
    </div>
  )
}
